/**
 * @fileoverview The Plants page's "Can plant now" filter (B84): which plants pass it. The server decides
 * which plants have a planting window that includes today (the plants brief's `planting.canPlantNow`); this only
 * reads that answer, so a plant out of window, with no window, or not yet answered does not pass.
 *
 * Where: plantViews (arrangePlants) and PlantGrid (builds the set from the brief).
 * API: none; the set comes from GET /plants/brief.
 * Settings: none.
 * Words (en/es): none; the filter's name is plants.toolbar.canPlantNow.
 *
 * @module canPlantNowFilter
 */

/** The brief's plants, as far as this filter reads them. */
export type PlantingFlags = Record<string, { planting: { canPlantNow: boolean } | null }>;

/**
 * The plants the server says can be planted now, by plant id.
 *
 * @param plants - The brief's plants by id.
 * @returns The ids whose planting window includes today.
 */
export function plantNowIds(plants: PlantingFlags): Set<number> {
  const ids = new Set<number>();
  for (const [id, entry] of Object.entries(plants)) {
    if (entry.planting?.canPlantNow) ids.add(Number(id));
  }
  return ids;
}

/**
 * Whether a plant passes the "Can plant now" filter.
 *
 * @param plantObjId - The plant's id.
 * @param plantNow - The ids the server said can be planted now; null while the brief is not in, when the filter has nothing to go on and passes everything.
 * @returns True when the plant may be shown.
 */
export function passesCanPlantNow(plantObjId: number, plantNow: Set<number> | null): boolean {
  if (plantNow === null) return true;
  return plantNow.has(plantObjId);
}
